import { action, computed, makeObservable, observable } from 'mobx';

import { ClaimFreeSpinsPayload, freeSpinsController, HttpResponse, HttpResponseStatus, launcherSocket } from '../api';
import { T, TWO_HOURS_MS, THIRTY_MINUTES_MS, FIVE_MINUTES } from '../constants';
import { NotificationPriority, SideMenuIcons } from '../enums';
import { HTTPResponse, UserFreebet } from '../models';
import { RootStore } from '.';


export class FreeSpinStore {

  private rootStore: RootStore;

  public freeSpinInfo: Array<UserFreebet> | null = null;

  public visibleFreeSpins: UserFreebet[] = [];

  public freeSpinAllRewardsOpen = false;

  public claimFreeSpinsResponse = new HttpResponse<HTTPResponse>({ result: null }, HttpResponseStatus.Default);

  /**
   * @description FREE_SPINS_EXPIRE_SOON alert state: a watcher polls the live free-spin data
   * every 5 minutes and fires each threshold alert once per freebet (tracked by id, so freebets
   * awarded mid-session alert too); because it always reads current data, claimed/used-up/removed
   * free spins stop alerting automatically. Not observable: nothing renders from these.
   */
  private expiryCheckIntervalId: NodeJS.Timeout | null = null;
  private twoHourAlertedIds = new Set<number>();
  private thirtyMinuteAlertedIds = new Set<number>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      freeSpinInfo: observable,
      visibleFreeSpins: observable,
      freeSpinAllRewardsOpen: observable,
      claimFreeSpinsResponse: observable,
      
      unseenFreeBets: computed,
      visibleFreeSpin: computed,
      
      setFreeSpinInfo: action.bound,
      closeFreeSpin: action.bound,
      removeFreeSpin: action.bound,
      markFreeSpinSeen: action.bound,
      closeAllFreeSpins: action.bound,
      showFreeSpinReward: action.bound,
      handleFreeBetCreated: action.bound,
      showAllFreeSpinRewards: action.bound,
      setClaimFreeSpinsResponse: action.bound,
    });
  }

  public setFreeSpinInfo(value: Array<UserFreebet> | null): void {
    this.freeSpinInfo = value;
    this.visibleFreeSpins = this.unseenFreeBets;
    this.freeSpinAllRewardsOpen = false;
    if (value?.length) this.startExpiryAlertsWatcher();
  }

  private startExpiryAlertsWatcher(): void {
    if (this.expiryCheckIntervalId) return;
    this.checkExpiringFreeSpins();
    this.expiryCheckIntervalId = setInterval(this.checkExpiringFreeSpins, FIVE_MINUTES);
  }

  private checkExpiringFreeSpins = (): void => {
    const now = Date.now();
    const expiringFreeSpins = (this.freeSpinInfo || []).filter(({ remainingCount, count, expireDate }) => (
      (remainingCount ?? count) > 0 && new Date(expireDate).getTime() > now
    ));

    // drop alert entries of freebets that are gone (claimed/used up/expired)
    const liveIds = new Set(expiringFreeSpins.map(({ id }) => id));
    this.twoHourAlertedIds.forEach((id) => { if (!liveIds.has(id)) this.twoHourAlertedIds.delete(id); });
    this.thirtyMinuteAlertedIds.forEach((id) => { if (!liveIds.has(id)) this.thirtyMinuteAlertedIds.delete(id); });

    // one alert per tick: the most urgent unalerted crossing fires and only that freebet is
    // marked - the others stay unmarked and take their turn on the next ticks
    const sortedByExpiry = [...expiringFreeSpins].sort((a, b) => (
      new Date(a.expireDate).getTime() - new Date(b.expireDate).getTime()
    ));

    for (const { id, expireDate } of sortedByExpiry) {
      const remainingTime = new Date(expireDate).getTime() - now;

      if (remainingTime <= THIRTY_MINUTES_MS && !this.thirtyMinuteAlertedIds.has(id)) {
        this.thirtyMinuteAlertedIds.add(id);
        // a freebet first seen this late gets only the urgent alert, never the milder one after it
        this.twoHourAlertedIds.add(id);
        this.sendExpiryNotification();
        return;
      }

      if (remainingTime > THIRTY_MINUTES_MS && remainingTime <= TWO_HOURS_MS && !this.twoHourAlertedIds.has(id)) {
        this.twoHourAlertedIds.add(id);
        this.sendExpiryNotification();
        return;
      }
    }
  };

  private sendExpiryNotification(): void {
    this.rootStore.sidebarMenuStore.setHasPromotionsNotification(true);
    this.rootStore.sidebarNotificationStore.enqueue(
      this.rootStore.translationsStore.translate(T.FREE_SPINS_EXPIRE_SOON),
      SideMenuIcons.Freespin,
      NotificationPriority.High
    );
  }

  public removeFreeSpin(id: number): void {
    this.freeSpinInfo = this.freeSpinInfo?.filter(freeSpin => freeSpin.id !== id) || null;
    this.visibleFreeSpins = [];
    this.freeSpinAllRewardsOpen = false;
  }

  public get visibleFreeSpin(): UserFreebet | undefined {
    return this.visibleFreeSpins[0];
  }

  public get unseenFreeBets(): UserFreebet[] {
    if (!this.freeSpinInfo) return [];
    return this.freeSpinInfo.filter(bet => bet?.seenByPlayer === false);
  }

  public closeFreeSpin(id: number, markAsSeen = false): void {
    this.visibleFreeSpins = [];
    this.freeSpinAllRewardsOpen = false;

    if (markAsSeen) {
      void this.markFreeSpinSeen(id);
    }
  }

  public closeAllFreeSpins(): void {
    this.visibleFreeSpins = [];
    this.freeSpinAllRewardsOpen = false;
  }

  public showFreeSpinReward(reward: UserFreebet): void {
    this.visibleFreeSpins = [reward];
    this.freeSpinAllRewardsOpen = false;
  }
  
  public showAllFreeSpinRewards(rewards: UserFreebet[] | null): void {
    this.visibleFreeSpins = rewards || [];
    this.freeSpinAllRewardsOpen = true;
  }

  public setClaimFreeSpinsResponse(value: HttpResponse<HTTPResponse>): void {
    this.claimFreeSpinsResponse = value;
  }

  public async claimFreeSpins(payload: ClaimFreeSpinsPayload): Promise<HTTPResponse> {
    this.setClaimFreeSpinsResponse(this.claimFreeSpinsResponse.fetching());
    try {
      const data = await freeSpinsController.claim(payload);
      this.setClaimFreeSpinsResponse(this.claimFreeSpinsResponse.fetched(data));
      return data;
    } catch (error) {
      // never leave it Pending - the claim button is disabled while it is, for every later popup too
      this.setClaimFreeSpinsResponse(this.claimFreeSpinsResponse.failed(String(error)));
      throw error;
    }
  }

  public async markFreeSpinSeen(id: number): Promise<HTTPResponse> {
    const data = await freeSpinsController.markAsSeen(id);
    this.setFreeSpinInfo(
      this.freeSpinInfo?.map(freeSpin => (
        freeSpin.id === id ? { ...freeSpin, seenByPlayer: true } : freeSpin
      )) || null
    );
    return data;
  }

  public async handleFreeBetCreated(data: UserFreebet): Promise<void> {
    const updatedList = [
      ...(this.freeSpinInfo || []),
      {
        ...data,
        seenByPlayer: false
      }
    ];
    this.setFreeSpinInfo(updatedList);
  }

  public listenEvents(): void {
    launcherSocket.onFreeBetCreated(this.handleFreeBetCreated.bind(this));
  }

}
